import type { APPLICANT_ATTRIBUTION_BOTH_TYPE, LOAN_REQUEST_COMMAND } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoanFormLayout } from './LoanFormLayout';
import { RefinanceFields } from './RefinanceFields';
import { useIsLoanFormDisabled } from './useIsLoanFormDisabled';
import type { SchemaCreditRequestForm } from '../../../schemas/creditRequestForm.schema';
import type { InstallmentLoanType } from '../../../types';
import { calculateDateAndRuntime } from '../../utils/calculateDateAndRuntime';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';
import { FormPercentInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormPercentInput';

type Props = {
    type: InstallmentLoanType;
    index: number;
    applicant: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    commandName: LOAN_REQUEST_COMMAND;
};

export const InstallmentLoanFields: React.FC<Props> = ({ applicant, commandName, index, type }) => {
    const { t } = useTranslation();
    const disabled = useIsLoanFormDisabled({ applicant, commandName });
    const {
        control,
        formState: { defaultValues },
        getValues,
        resetField,
        setValue,
    } = useFormContext<SchemaCreditRequestForm>();
    const name = `${type}.${index}` as const;

    const handleCalculation = (triggerType: 'kreditLaufzeit' | 'datumErsteZahlung' | 'datumLetzteRate') => {
        // setTimeout lets the input commit its value before the calculation reads the form state
        setTimeout(() => {
            const loanData = getValues(name);
            if (!loanData) return;
            calculateDateAndRuntime({
                defaultValue: defaultValues?.[type]?.[index],
                index,
                loanData,
                resetField,
                setValue,
                triggerType,
                type,
            });
        }, 0);
    };

    return (
        <LoanFormLayout
            name={name}
            disabled={disabled}
            left={
                <>
                    <FormCurrencyInput
                        control={control}
                        id={`${name}.urspruenglicherKreditbetrag`}
                        label={t('EXISTING_LOANS.LOAN_ORIGINAL_AMOUNT')}
                        disabled={disabled}
                    />
                    <FormCurrencyInput
                        control={control}
                        id={`${name}.rateMonatlich`}
                        label={t('EXISTING_LOANS.LOAN_MONTHLY_RATE')}
                        disabled={disabled}
                    />
                    <FormCurrencyInput
                        control={control}
                        id={`${name}.restschuld`}
                        label={t('EXISTING_LOANS.LOAN_RESIDUAL_DEBT')}
                        disabled={disabled}
                    />
                    <FormCurrencyInput
                        control={control}
                        id={`${name}.schlussrate`}
                        label={t('EXISTING_LOANS.LOAN_FINAL_RATE')}
                        disabled={disabled}
                    />
                    <FormDateInput
                        control={control}
                        id={`${name}.datumErsteZahlung`}
                        label={t('EXISTING_LOANS.LOAN_FIRST_PAYMENT_DATE')}
                        onValueChange={() => handleCalculation('datumErsteZahlung')}
                        disabled={disabled}
                    />
                    <FormDateInput
                        control={control}
                        id={`${name}.datumLetzteRate`}
                        label={t('EXISTING_LOANS.LOAN_LAST_RATE_DATE')}
                        onValueChange={() => handleCalculation('datumLetzteRate')}
                        disabled={disabled}
                    />
                    <FormNumberInput
                        control={control}
                        id={`${name}.kreditLaufzeit`}
                        label={t('EXISTING_LOANS.LOAN_RUNTIME')}
                        allowDecimals={false}
                        onValueChange={() => handleCalculation('kreditLaufzeit')}
                        disabled={disabled}
                    />
                    <FormPercentInput
                        control={control}
                        id={`${name}.zinssatz`}
                        label={t('EXISTING_LOANS.LOAN_INTEREST')}
                        allowDecimals
                        disabled={disabled}
                    />
                </>
            }
            right={<RefinanceFields name={name} disabled={disabled} />}
        />
    );
};
